/**
 * Cart Gift Countdown JavaScript
 *
 * This script handles the countdown timer for cart gift promotions.
 * When the countdown reaches zero, it hides the gift popup and refreshes the cart.
 */

const COUNTDOWN_REINIT_DELAY_MS = 200;

let countdownInterval;
let currentCountdownElement = null;
let deadlineReached = false;

// Debounced wrapper to prevent multiple rapid re-initializations (issues #7, #23)
let _countdownDebounceTimer = null;
function debouncedInitializeCountdown() {
  clearTimeout(_countdownDebounceTimer);
  _countdownDebounceTimer = setTimeout(initializeCountdown, COUNTDOWN_REINIT_DELAY_MS);
}

function initializeCountdown() {
  // Clear any existing interval
  if (countdownInterval) {
    clearInterval(countdownInterval);
    countdownInterval = null;
  }

  const countdownElement = document.getElementById('cart-gift-countdown');

  if (!countdownElement) {
    currentCountdownElement = null;
    return; // No countdown element found
  }

  // Always reinitialize for cart updates (element might be recreated)
  // Remove the same-element check for cart updates

  currentCountdownElement = countdownElement;
  deadlineReached = false;

  const deadlineString = countdownElement.getAttribute('data-deadline');

  if (!deadlineString) {
    console.warn('Cart gift countdown: No deadline specified');
    return;
  }

  // Check immediately if deadline has expired and hide the entire gift card.
  // Note: This deadline check is intentionally duplicated in cart-gift.liquid (JS) and
  // auto-cart-gift.liquid (JS) as defensive programming -- each component guards itself.
  const now = new Date().getTime();
  const deadline = new Date(deadlineString).getTime();
  if (now >= deadline) {
    // Hide the entire gift card wrapper immediately
    const giftWrapper = document.querySelector(
      '.gift-card-outer-wrapper.countdown-active'
    );
    if (giftWrapper) {
      giftWrapper.style.display = 'none';
    }
    return;
  }

  function updateCountdown() {
    if (deadlineReached) return;

    // Check if element still exists (might have been removed during DOM update)
    if (
      !currentCountdownElement ||
      !document.contains(currentCountdownElement)
    ) {
      if (countdownInterval) {
        clearInterval(countdownInterval);
        countdownInterval = null;
      }
      return;
    }

    const now = new Date().getTime();
    const deadline = new Date(deadlineString).getTime();
    const timeLeft = deadline - now;

    if (timeLeft <= 0) {
      // Countdown has expired
      deadlineReached = true;
      if (countdownInterval) {
        clearInterval(countdownInterval);
        countdownInterval = null;
      }
      const prefix = currentCountdownElement.dataset.prefix || '';
      const separators =
        currentCountdownElement.getAttribute('data-separators') || 'd,h,m,s';
      const [, hourSep, minSep, secSep] = separators.split(',');
      currentCountdownElement.textContent =
        prefix + '00' + hourSep + ' 00' + minSep + ' 00' + secSep;

      // Hide the cart gift popup
      hideCartGiftPopup();

      // Trigger cart refresh to ensure gift item is not available
      refreshCart();

      return;
    }

    // Calculate days, hours, minutes, seconds
    const days = Math.floor(timeLeft / (1000 * 60 * 60 * 24));
    const hours = Math.floor((timeLeft / (1000 * 60 * 60)) % 24);
    const minutes = Math.floor((timeLeft / (1000 * 60)) % 60);
    const seconds = Math.floor((timeLeft / 1000) % 60);

    // Get separators from data attribute
    const separators =
      currentCountdownElement.getAttribute('data-separators') || 'd,h,m,s';
    const [daySep, hourSep, minSep, secSep] = separators.split(',');

    // Format based on whether days are needed
    let formattedTime;
    if (days > 0) {
      // Include days: "2d 14h 30m 15s"
      formattedTime = [
        days + daySep,
        hours.toString().padStart(2, '0') + hourSep,
        minutes.toString().padStart(2, '0') + minSep,
        seconds.toString().padStart(2, '0') + secSep,
      ].join(' ');
    } else {
      // Always use separators: "14h 30m 15s"
      formattedTime = [
        hours.toString().padStart(2, '0') + hourSep,
        minutes.toString().padStart(2, '0') + minSep,
        seconds.toString().padStart(2, '0') + secSep,
      ].join(' ');
    }

    // Preserve the prefix and append the countdown time
    const prefix = currentCountdownElement.dataset.prefix || '';
    currentCountdownElement.textContent = prefix + formattedTime;
  }

  function hideCartGiftPopup() {
    // Hide both maximized and minimized gift wrappers
    const maximizedWrapper = document.querySelector('.gift-card-outer-wrapper');
    const minimizedWrapper = document.querySelector(
      '.gift-card-minimized-wrapper'
    );
    const disclaimer = document.querySelector('.discount-disclaimer');

    if (maximizedWrapper) {
      maximizedWrapper.style.display = 'none';
    }
    if (minimizedWrapper) {
      minimizedWrapper.style.display = 'none';
    }
    if (disclaimer) {
      disclaimer.style.display = 'none';
    }
  }

  function refreshCart() {
    // Trigger a cart update by fetching the cart data
    // This ensures the gift item is not offered if it was previously added
    fetch('/cart.js')
      .then((response) => response.json())
      .then((cart) => {
        // Dispatch cart update event to notify other components
        if (typeof publish === 'function') {
          publish('cart-update', { cart: cart });
        } else {
          // Fallback: dispatch custom event
          window.dispatchEvent(
            new CustomEvent('cart-update', { detail: { cart: cart } })
          );
        }
      })
      .catch((error) => {
        console.warn('Failed to refresh cart after countdown expiry:', error);
      });
  }

  // Start the countdown immediately
  updateCountdown();

  // Update every second
  countdownInterval = setInterval(updateCountdown, 1000);
}

// Initialize countdown on DOMContentLoaded
document.addEventListener('DOMContentLoaded', function () {
  initializeCountdown();
});

// Use MutationObserver to detect when countdown element is added/re-added to DOM
const observer = new MutationObserver((mutations) => {
  mutations.forEach((mutation) => {
    mutation.addedNodes.forEach((node) => {
      if (node.nodeType === 1) {
        // Element node
        // Check if the added node contains our countdown element
        const countdownElement =
          node.id === 'cart-gift-countdown'
            ? node
            : node.querySelector('#cart-gift-countdown');
        if (countdownElement) {
          // Element was added, reinitialize countdown (debounced)
          debouncedInitializeCountdown();
        }
      }
    });
  });
});

// Start observing (scoped to cart drawer to reduce unnecessary callbacks -- issue #31)
var countdownObserverTarget = document.querySelector('cart-drawer') || document.body;
observer.observe(countdownObserverTarget, {
  childList: true,
  subtree: true,
});

// Also try the subscribe method if available
if (typeof subscribe === 'function') {
  const cartUpdateUnsubscriber = subscribe('cart-update', () => {
    // Reinitialize countdown after cart update (debounced)
    debouncedInitializeCountdown();
  });

  // Cleanup on page unload
  window.addEventListener('beforeunload', function () {
    if (cartUpdateUnsubscriber) {
      cartUpdateUnsubscriber();
    }
    if (countdownInterval) {
      clearInterval(countdownInterval);
    }
    observer.disconnect();
  });
}
